import React from 'react';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {DataGrid, GridColDef, GridRowSelectionModel, GridSortModel} from '@mui/x-data-grid';
import {GridPaginationModel} from "@mui/x-data-grid/models/gridPaginationProps";
import {PaymentAdviceDetails, PaymentAdviceResponse, PaymentAdviceSummary} from "../../api/PaymentAdviceApi";
import styles from './PaymentAdvice.module.css';

export const columns: GridColDef[] = [
    {
        field: 'pspReference', headerName: 'ID', width: 184, sortable: false, filterable: false, valueGetter: (value: string, row: PaymentAdviceResponse) => {
            return value?.trim()?.length ? value : row.id;
        }
    },
    {field: 'referenceDocument', headerName: 'Reference Doc', width: 194, sortable: false, filterable: false},
    {field: 'merchantAccount', headerName: 'Merchant', width: 224, sortable: true, filterable: false},
    {field: 'amount', headerName: 'Amount', width: 130, sortable: true, type: 'number', filterable: false, valueFormatter: (value: number) => value.toFixed(2)},
    {field: 'currency', headerName: 'Currency', width: 90, sortable: true, filterable: false},
    {field: 'transactionDate', headerName: 'Transaction Date', width: 165, sortable: true, filterable: false},
    {field: 'reportDate', headerName: 'Report Date', width: 164, sortable: true, filterable: false},
    {field: 'financialTransactionType', headerName: 'Type', width: 120, sortable: false, filterable: false},
    {field: 'status', headerName: 'Status', width: 150, sortable: false, filterable: false},
];

interface PaymentAdviceDataGridProps {
    rows: PaymentAdviceSummary;
    loading: boolean;
    sortModel: GridSortModel;
    pageModel: GridPaginationModel;
    onSortModelChange: (model: GridSortModel) => void;
    onPaginationModelChange: (model: GridPaginationModel) => void;
    onRowClick: (params: any) => void;
    height: number;
    onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
}

interface PaymentAdviceDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    details: PaymentAdviceDetails | null;
}

const PaymentAdviceDetailsDialog: React.FC<PaymentAdviceDetailsDialogProps> = ({open, onClose, details}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Payment Advice Details</DialogTitle>
            <DialogContent dividers>
                {details ? (
                    <Box>
                        {details.pspReference && <p><strong>Transaction ID:</strong> {details.pspReference}</p>}
                        {details.disbursementReference && <p><strong>Disbursement reference:</strong> {details.disbursementReference}</p>}
                        {details.referenceDocument && <p><strong>Reference:</strong> {details.referenceDocument}</p>}
                        {details.currency !== "" && details.amount != null && details.amount !== 0 && (
                            <p><strong>Amount:</strong> {details.amount.toFixed(2)} {details.currency}</p>)}
                        {details.transactionDate && <p><strong>Date:</strong> {details.transactionDate}</p>}
                        {details.merchantAccount && <p><strong>Merchant:</strong> {details.merchantAccount}</p>}
                        {details.currency !== "" && details.feeAmountInTransactionCurrency != null && details.feeAmountInTransactionCurrency !== 0 && (
                            <p><strong>Fee:</strong> {details.feeAmountInTransactionCurrency.toFixed(2)} {details.currency}</p>)}
                        {details.transactionFeePercentage && (
                            <p><strong>Fee percentage:</strong> {details.transactionFeePercentage}%</p>)}
                        {details.surchargeAmountInTransCrcy && (
                            <p><strong>Surcharge:</strong> {details.surchargeAmountInTransCrcy.toFixed(2)} {details.currency}</p>)}
                        {details.taxAmount && (
                            <p><strong>Tax:</strong> {details.taxAmount.toFixed(2)} {details.currency}</p>)}
                        {details.originalCurrencyAmount != null && details.originalCurrencyAmount !== 0 && (
                            <p><strong>Amount with
                                fee{details.currency !== details.originalCurrency ? " (in original currency)" : ""}:</strong> {details.originalCurrencyAmount.toFixed(2)} {details.originalCurrency}
                            </p>)}
                        {details.originalReference && (
                            <p><strong>Original reference:</strong> {details.originalReference}</p>)}
                        {details.paymentType && <p><strong>Payment type:</strong> {details.paymentType}</p>}
                        {details.paymentMethod && <p><strong>Payment method:</strong> {details.paymentMethod}</p>}
                        {details.financialTransactionType && (
                            <p><strong>Type:</strong> {details.financialTransactionType}</p>)}
                        {details.financialTransactionType === "CHARGE" &&
                            <p><strong>Direct capture:</strong> {details.directCapture ? 'Yes' : 'No'}</p>}
                        {details.reportDate && <p><strong>Report date:</strong> {details.reportDate}</p>}
                        {details.status && <p><strong>Status:</strong> {details.status}</p>}
                        {details.status === "EXPORTED" && details.statusUpdatedAt ? (
                          <p><strong>Exported at:</strong> {details.statusUpdatedAt}</p>
                        ) : details.status === "INVALID" && details.lastReason ? (
                          <p><strong>Reason:</strong> {details.lastReason}</p>
                        ) : null}
                    </Box>
                ) : (
                    <p>Loading...</p>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const PaymentAdviceDataGrid: React.FC<PaymentAdviceDataGridProps> = ({
                                                                         rows,
                                                                         loading,
                                                                         sortModel,
                                                                         pageModel,
                                                                         onSortModelChange,
                                                                         onPaginationModelChange,
                                                                         onRowClick,
                                                                         height,
                                                                         onRowSelectionModelChange
                                                                     }) => {
    return (
        <Box
            className={styles.gridWrapper}
            style={{height: `${height}px`}}
        >
            <DataGrid
                className={styles.dataGridRoot}
                classes={{
                    row: styles.dataGridRow,
                    cell: styles.dataGridCell
                }}
                rows={rows.entries}
                columns={columns}
                loading={loading}
                getRowId={(row: PaymentAdviceResponse) => row.id}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                onRowClick={onRowClick}
                paginationMode="server"
                nonce={(window as any).__CSP_NONCE__}
                rowCount={rows.totalElements}
                pageSizeOptions={[25, 50, 100]}
                paginationModel={pageModel}
                onPaginationModelChange={onPaginationModelChange}
                disableRowSelectionOnClick={true}
                checkboxSelection
                onRowSelectionModelChange={onRowSelectionModelChange}
            />
        </Box>
    );
};

export {PaymentAdviceDataGrid, PaymentAdviceDetailsDialog};
